import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ViewAction } from "@/components/table/actions";

import { useState } from "react";
import { Table } from "@/components/table";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, UserIcon } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { startCase } from "lodash";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useGetEvaluatorsByEvaluationIdQuery } from "@/services/(company)/api.performance";
import type { IEvaluator } from "@/types/(company)/performance.interface";
import { PATHS } from "@/routes/paths";

enum ENUM_PERFORMANCE_CYCLE_STATUS {
  COMPLETED = "completed",
  IN_PROGRESS = "in_progress",
  PENDING = "pending",
  CANCELLED = "cancelled",
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildColumns = (t: TFunction) => [
  { key: "evaluatorName", label: t("company:performance.col_evaluator_name") },
  { key: "role", label: t("company:performance.col_role_in_team") },
  { key: "score", label: t("company:performance.col_score") },
  { key: "weightedScore", label: t("company:performance.col_weighted_score") },
  {
    key: "performanceEvaluation",
    label: t("company:performance.col_performance_evaluation"),
  },
  {
    key: "evaluationStatus",
    label: t("company:performance.col_evaluation_status"),
  },
  {
    key: "actions",
    label: t("company:performance.col_actions"),
    className: "w-1",
  },
];

const getProgressColor = (percent: number) => {
  if (percent >= 70) return "var(--color-success)";
  if (percent >= 40) return "var(--color-danger)";
  return "var(--color-danger)";
};

// Compute performance percent: weightedScore out of totalWeightage (0–100 scale)
const computePerformancePercent = (evaluator: IEvaluator): number => {
  if (!evaluator.totalWeightage || evaluator.totalWeightage === 0) return 0;
  return Math.round((evaluator.weightedScore / evaluator.totalWeightage) * 100);
};

const isActionDisabled = (status: ENUM_PERFORMANCE_CYCLE_STATUS) =>
  status === ENUM_PERFORMANCE_CYCLE_STATUS.PENDING ||
  status === ENUM_PERFORMANCE_CYCLE_STATUS.IN_PROGRESS;

// ─── Component ────────────────────────────────────────────────────────────────

export const PerformanceEvaluatorsList = () => {
  const { t } = useTranslation(["company", "common"]);
  const columns = buildColumns(t);
  const { id, cycleId } = useParams<{ id: string; cycleId: string }>();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({ page: 1, limit: 10 });

  const { data, isFetching: isLoading } = useGetEvaluatorsByEvaluationIdQuery(
    { evaluationId: String(cycleId) },
    { skip: !cycleId },
  );

  const evaluators: IEvaluator[] = data?.data ?? [];
  const totalPages: number = data?.meta?.totalPages ?? 1;

  // Derive overall stats from the evaluators list
  const totalEvaluators = evaluators.length;
  const completedEvaluators = evaluators.filter(
    (e) => e.status === ENUM_PERFORMANCE_CYCLE_STATUS.COMPLETED,
  ).length;

  // Overall score: average weightedScore across completed evaluators
  const completedList = evaluators.filter(
    (e) => e.status === ENUM_PERFORMANCE_CYCLE_STATUS.COMPLETED,
  );
  const overallScore =
    completedList.length > 0
      ? Math.round(
          completedList.reduce(
            (sum, e) => sum + computePerformancePercent(e),
            0,
          ) / completedList.length,
        )
      : 0;

  // Use the first evaluator's user as the subject (the person being evaluated)
  const subjectUser = evaluators[0]?.user ?? null;

  const rows = evaluators.map((evaluator) => {
    const performancePercent = computePerformancePercent(evaluator);

    return {
      id: evaluator.id,
      evaluatorName: (
        <div className="flex items-center gap-2.5">
          <Avatar className="h-8 w-8 rounded-xs shrink-0">
            <AvatarImage
              src={evaluator.evaluator?.profileImage}
              className="rounded-xs object-cover"
            />
            <AvatarFallback className="rounded-xs bg-border">
              <UserIcon size={14} className="text-gray-500" />
            </AvatarFallback>
          </Avatar>
          <span className="text-sm text-gray-800">
            {evaluator.evaluator?.fullName ?? "-"}
          </span>
        </div>
      ),
      role: evaluator?.evaluator?.teamRole?.roleName ?? "-",
      score: (
        <span className="text-sm text-gray-700">
          {evaluator.totalScore}/100
        </span>
      ),
      weightedScore: (
        <span className="text-sm text-gray-700">
          {evaluator.weightedScore ?? 0}
        </span>
      ),
      performanceEvaluation: (
        <div className="flex items-center gap-2 min-w-[140px]">
          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${performancePercent}%`,
                backgroundColor:
                  performancePercent === 0
                    ? "var(--color-border)"
                    : getProgressColor(performancePercent),
              }}
            />
          </div>
          <span className="text-xs text-gray-500 w-8 text-end">
            {performancePercent}%
          </span>
        </div>
      ),
      evaluationStatus: <StatusPill status={evaluator.status} />,
      actions: (
        <div className="flex gap-2 items-center justify-end">
          <ViewAction
            onClick={() =>
              navigate(
                `${PATHS.COMPANY.COMPANY_PERFORMANCE_PATHS.PERFORMANCE_REVIEW(id!, evaluator.id)}?type=company`,
              )
            }
            disabled={isActionDisabled(evaluator.status)}
            tooltip={t("company:performance.view_details")}
            className="disabled:opacity-40"
          />
        </div>
      ),
    };
  });

  return (
    <section className="flex flex-col gap-4 bg-white p-4 rounded-sm">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="h-9 w-9 flex items-center justify-center rounded-xs border border-border shrink-0"
        >
          <ChevronLeft size={18} className="text-primary rtl:rotate-180" />
        </button>
        <h1 className="text-lg font-semibold text-gray-800">
          {t("company:performance.list_of_evaluators")}
        </h1>
      </div>

      <Card className="overflow-hidden">
        {/* Card header: subject user info + overall score */}
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3 bg-info-muted p-4">
          {/* Subject user info */}
          <div className="flex items-center gap-3">
            <Avatar className="shrink-0">
              <AvatarImage src={subjectUser?.profileImage ?? undefined} />
              <AvatarFallback className="bg-border">
                <UserIcon size={12} className="text-gray-500" />
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-medium text-gray-800">
                {subjectUser?.fullName ?? "-"}
              </p>
            </div>
          </div>

          {/* Overall Score */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {t("company:performance.overall_score_label", {
                completed: completedEvaluators,
                total: totalEvaluators,
              })}
            </span>
            <OverallScoreCircle percent={overallScore} />
          </div>
        </CardHeader>

        <CardContent className="p-4">
          <Table
            noPadding
            columns={columns}
            data={rows}
            pagination={{
              totalPages,
              currentPage: filters.page,
              onPageChange: (page) => setFilters((prev) => ({ ...prev, page })),
            }}
            loading={isLoading}
            onRowClick={(row) =>
              navigate(
                `${PATHS.COMPANY.COMPANY_PERFORMANCE_PATHS.PERFORMANCE_REVIEW(id!, row.id)}?type=company`,
              )
            }
          />
        </CardContent>
      </Card>
    </section>
  );
};

// ─── Overall Score Circle ─────────────────────────────────────────────────────

const OverallScoreCircle = ({ percent }: { percent: number }) => {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative h-12 w-12 flex items-center justify-center">
      <svg
        className="absolute inset-0 -rotate-90"
        width="48"
        height="48"
        viewBox="0 0 48 48"
      >
        {/* Track */}
        <circle
          cx="24"
          cy="24"
          r={radius}
          fill="none"
          stroke="var(--color-border)"
          strokeWidth="4"
        />
        {/* Progress */}
        <circle
          cx="24"
          cy="24"
          r={radius}
          fill="none"
          stroke="var(--color-success)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500"
        />
      </svg>
      <span className="text-xs font-semibold text-gray-800 z-10">
        {percent}%
      </span>
    </div>
  );
};

// ─── Status Pill ─────────────────────────────────────────────────────────────

const StatusPill = ({ status }: { status: ENUM_PERFORMANCE_CYCLE_STATUS }) => {
  const { t } = useTranslation(["company", "common"]);
  const statusPills: Record<ENUM_PERFORMANCE_CYCLE_STATUS, string> = {
    [ENUM_PERFORMANCE_CYCLE_STATUS.COMPLETED]:
      "bg-green-100 text-green-700 border border-green-300",
    [ENUM_PERFORMANCE_CYCLE_STATUS.IN_PROGRESS]:
      "bg-orange-50 text-orange-500 border border-orange-300",
    [ENUM_PERFORMANCE_CYCLE_STATUS.PENDING]:
      "bg-yellow-50 text-yellow-600 border border-yellow-300",
    [ENUM_PERFORMANCE_CYCLE_STATUS.CANCELLED]:
      "bg-red-100 text-red-600 border border-red-300",
  };

  const className =
    statusPills[status] ?? "bg-gray-100 text-gray-600 border border-border";

  const label: Record<ENUM_PERFORMANCE_CYCLE_STATUS, string> = {
    [ENUM_PERFORMANCE_CYCLE_STATUS.COMPLETED]: t(
      "company:performance.cycle_status_completed",
    ),
    [ENUM_PERFORMANCE_CYCLE_STATUS.IN_PROGRESS]: t(
      "company:performance.cycle_status_in_progress",
    ),
    [ENUM_PERFORMANCE_CYCLE_STATUS.PENDING]: t(
      "company:performance.cycle_status_pending",
    ),
    [ENUM_PERFORMANCE_CYCLE_STATUS.CANCELLED]: t(
      "company:performance.cycle_status_cancelled",
    ),
  };

  return (
    <Badge className={`font-medium rounded-sm ${className}`}>
      {label[status] ?? startCase(status)}
    </Badge>
  );
};
